import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  CalendarDays,
  CircleHelp,
  History,
  House,
  Settings,
  Sparkles,
  Users,
} from "lucide-react";
import {
  getInstructorClientRoute,
  getInstructorClientTabFromPath,
  ROUTES,
  type InstructorClientTab,
} from "../constants/routes";

export type AppNavItem = {
  to: string;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Render this item with a prominent, attention-grabbing treatment. */
  highlight?: boolean;
  /** Set on the client-chart tabs so the sidebar can group them under the client's name. */
  clientTab?: InstructorClientTab;
};

export const APP_NAV_ITEMS: AppNavItem[] = [
  {
    to: ROUTES.gettingStarted,
    label: "Get Started",
    description: "Finish setting up",
    icon: Sparkles,
    highlight: true,
  },
  {
    to: ROUTES.observations,
    label: "Observations",
    description: "Track and edit entries",
    icon: House,
  },
  {
    to: ROUTES.calendar,
    label: "Calendar",
    description: "Review your cycle",
    icon: CalendarDays,
  },
  {
    to: ROUTES.dashboard,
    label: "Insights",
    description: "Trends and analytics",
    icon: BarChart3,
  },
  {
    to: ROUTES.cycleHistory,
    label: "History",
    description: "Past cycles",
    icon: History,
  },
  {
    to: ROUTES.settings,
    label: "Settings",
    description: "Profile and account",
    icon: Settings,
  },
];

export type InstructorClientTabItem = {
  tab: InstructorClientTab;
  label: string;
  description: string;
  icon: LucideIcon;
};

export const INSTRUCTOR_CLIENT_TABS: InstructorClientTabItem[] = [
  {
    tab: "calendar",
    label: "Calendar",
    description: "Chart by cycle day",
    icon: CalendarDays,
  },
  {
    tab: "insights",
    label: "Insights",
    description: "Trends and analytics",
    icon: BarChart3,
  },
  {
    tab: "history",
    label: "History",
    description: "Past cycles",
    icon: History,
  },
];

const INSTRUCTOR_NAV_HEAD: AppNavItem[] = [
  {
    to: ROUTES.instructorClients,
    label: "Clients",
    description: "Open a client's chart",
    icon: Users,
  },
];

const INSTRUCTOR_NAV_TAIL: AppNavItem[] = [
  {
    to: ROUTES.settings,
    label: "Settings",
    description: "Profile and account",
    icon: Settings,
  },
  {
    to: ROUTES.help,
    label: "Help",
    description: "Answers and support",
    icon: CircleHelp,
  },
];

/**
 * One shell, two navs. Instructors have no daily-entry surface (Observations, Bulk Entry, Getting
 * Started): their home is the roster, and the client's Calendar / Insights / History appear only
 * while a client is open. Everyone else gets the personal nav unchanged.
 */
export const getNavItems = ({
  isInstructor,
  activeClientId,
}: {
  isInstructor: boolean;
  activeClientId: string | null;
}): AppNavItem[] => {
  if (!isInstructor) {
    return APP_NAV_ITEMS;
  }

  const clientItems: AppNavItem[] = activeClientId
    ? INSTRUCTOR_CLIENT_TABS.map((item) => ({
        to: getInstructorClientRoute(activeClientId, item.tab),
        label: item.label,
        description: item.description,
        icon: item.icon,
        clientTab: item.tab,
      }))
    : [];

  return [...INSTRUCTOR_NAV_HEAD, ...clientItems, ...INSTRUCTOR_NAV_TAIL];
};

type PageMeta = {
  title: string;
  subtitle: string;
};

const PAGE_META: Record<string, PageMeta> = {
  [ROUTES.gettingStarted]: {
    title: "Get Started",
    subtitle: "Finish setting up your account.",
  },
  [ROUTES.observations]: {
    title: "Observations",
    subtitle: "Track daily signs and save observations.",
  },
  [ROUTES.legacyToday]: {
    title: "Observations",
    subtitle: "Track daily signs and save observations.",
  },
  [ROUTES.calendar]: {
    title: "Calendar",
    subtitle: "Review cycle events and day-by-day history.",
  },
  [ROUTES.dashboard]: {
    title: "Insights",
    subtitle: "Understand trends across your cycle data.",
  },
  [ROUTES.cycleHistory]: {
    title: "Cycle History",
    subtitle: "Explore previous cycles and summary stats.",
  },
  [ROUTES.instructorClients]: {
    title: "Clients",
    subtitle: "Open a client's chart.",
  },
  [ROUTES.settings]: {
    title: "Settings",
    subtitle: "Manage your profile, reminders, and account.",
  },
  [ROUTES.subscription]: {
    title: "Subscription",
    subtitle: "Manage plan details and billing.",
  },
  [ROUTES.help]: {
    title: "Help",
    subtitle: "Find answers and support resources.",
  },
};

// The client-chart tabs reuse the personal page titles on purpose: whose chart it is lives in the
// ClientViewBanner directly under the header, not in the title.
const CLIENT_TAB_PAGE_META: Record<InstructorClientTab, PageMeta> = {
  calendar: PAGE_META[ROUTES.calendar],
  insights: PAGE_META[ROUTES.dashboard],
  history: { title: "History", subtitle: PAGE_META[ROUTES.cycleHistory].subtitle },
};

export const getPageMeta = (pathname: string): PageMeta => {
  const clientTab = getInstructorClientTabFromPath(pathname);
  if (clientTab) {
    return CLIENT_TAB_PAGE_META[clientTab];
  }

  const match = Object.keys(PAGE_META).find(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  );

  return match
    ? PAGE_META[match]
    : {
        title: "Tracker",
        subtitle: "",
      };
};
